import type {
  CreateManagedReservationRequestDto,
  CreateWaitlistEntryRequestDto,
  RecordReservationDepositRequestDto,
  SendReservationConfirmationRequestDto,
} from "./dtos.ts";

function isIsoDate(value: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function isPositiveNumber(value: number) {
  return Number.isFinite(value) && value > 0;
}

export function validateCreateManagedReservationRequest(input: CreateManagedReservationRequestDto) {
  const errors: Record<string, string> = {};

  if (!input.guestId.trim()) errors.guestId = "Guest is required.";
  if (!input.guestLabel.trim()) errors.guestLabel = "Guest label is required.";
  if (!isIsoDate(input.arrival)) errors.arrival = "Arrival must be a valid ISO date.";
  if (!isIsoDate(input.departure)) errors.departure = "Departure must be a valid ISO date.";
  if (isIsoDate(input.arrival) && isIsoDate(input.departure) && input.arrival > input.departure) {
    errors.departure = "Departure must be on or after arrival.";
  }
  if (!input.roomTypeId.trim()) errors.roomTypeId = "Room type is required.";
  if (!isPositiveNumber(input.rooms)) errors.rooms = "Rooms must be greater than 0.";
  if (!isPositiveNumber(input.pricingSnapshot.totalAmount)) errors.totalAmount = "Total amount must be greater than 0.";
  if (!input.pricingSnapshot.currency.trim()) errors.currency = "Currency is required.";

  if (input.sendConfirmationEmail && !input.guestEmail?.trim()) {
    errors.guestEmail = "Recipient email is required to send a confirmation.";
  }

  if (input.depositPolicy.type === "percentage") {
    if (!isPositiveNumber(input.depositPolicy.percentage ?? 0)) {
      errors.depositPercentage = "Deposit percentage must be greater than 0.";
    }
  }

  if (input.depositPolicy.type === "fixed") {
    if (!isPositiveNumber(input.depositPolicy.fixedAmount ?? 0)) {
      errors.depositFixedAmount = "Fixed deposit amount must be greater than 0.";
    }
  }

  return errors;
}

export function validateRecordReservationDepositRequest(input: RecordReservationDepositRequestDto) {
  const errors: Record<string, string> = {};
  if (!input.reservationId.trim()) errors.reservationId = "Reservation is required.";
  if (!isPositiveNumber(input.amount)) errors.amount = "Deposit amount must be greater than 0.";
  if (!input.currency.trim()) errors.currency = "Currency is required.";
  return errors;
}

export function validateSendReservationConfirmationRequest(input: SendReservationConfirmationRequestDto) {
  const errors: Record<string, string> = {};
  if (!input.reservationId.trim()) errors.reservationId = "Reservation is required.";
  if (!input.recipientEmail.trim()) {
    errors.recipientEmail = "Recipient email is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.recipientEmail)) {
    errors.recipientEmail = "Recipient email is invalid.";
  }
  return errors;
}

export function validateCreateWaitlistEntryRequest(input: CreateWaitlistEntryRequestDto) {
  const errors: Record<string, string> = {};
  if (!input.guestLabel.trim()) errors.guestLabel = "Guest label is required.";
  if (!isIsoDate(input.arrival)) errors.arrival = "Arrival must be a valid ISO date.";
  if (!isIsoDate(input.departure)) errors.departure = "Departure must be a valid ISO date.";
  if (isIsoDate(input.arrival) && isIsoDate(input.departure) && input.arrival > input.departure) {
    errors.departure = "Departure must be on or after arrival.";
  }
  if (!input.roomTypeId.trim()) errors.roomTypeId = "Room type is required.";
  if (!isPositiveNumber(input.rooms)) errors.rooms = "Rooms must be greater than 0.";
  if (!Number.isFinite(input.priority) || input.priority < 1) errors.priority = "Priority must be at least 1.";
  return errors;
}

